'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { useComparison } from '@/contexts/ComparisonContext';

export default function StickyCompareBar() {
  const router = useRouter();
  const { selectedItems } = useComparison();

  if (selectedItems.length === 0) return null;

  const handleCompareClick = () => {
    router.push('/compare');
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[200] flex items-center justify-between"
      style={{
        background: 'rgba(255, 255, 255, 0.99)',
        backdropFilter: 'blur(40px) saturate(260%)',
        WebkitBackdropFilter: 'blur(40px) saturate(260%)',
        borderTop: '3px solid #D90012',
        boxShadow: '0 -10px 48px rgba(0,0,0,0.16), 0 -2px 0 rgba(217,0,18,0.4)',
        animation: 'slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        padding: '14px 36px',
      }}
    >
      {/* Selected thumbnails + info */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2.5">
          {selectedItems.map((item) => (
            <div
              key={item.id}
              className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0"
              style={{
                background: 'rgba(0,0,0,0.05)',
                border: '2px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
              />
            </div>
          ))}
        </div>
        <div>
          <div className="font-black text-sm hidden sm:block" style={{ color: '#0A0A0A', letterSpacing: '-0.03em', lineHeight: 1.18 }}>
            {selectedItems.length} paddle{selectedItems.length > 1 ? 's' : ''} selected
          </div>
          <div className="text-xs hidden sm:block" style={{ color: 'rgba(10,10,10,0.4)', marginTop: 3, lineHeight: 1.4 }}>
            {selectedItems.length >= 2 ? (
              <span style={{ color: '#D90012', fontWeight: 800 }}>Ready to compare</span>
            ) : 'Add one more to compare'}
          </div>
        </div>
      </div>

      {/* Right side: names + compare button */}
      <div className="flex items-center gap-7">
        {/* Paddle names — hidden on small screens */}
        {selectedItems.length >= 2 && (
          <div className="hidden md:flex items-center gap-3.5">
            {selectedItems.map((item, i) => (
              <React.Fragment key={item.id}>
                {i > 0 && (
                  <span
                    className="text-xs font-black px-2.5 py-1 rounded-lg"
                    style={{ color: '#D90012', letterSpacing: '0.07em', background: 'rgba(217,0,18,0.08)', border: '1.5px solid rgba(217,0,18,0.18)' }}
                  >vs</span>
                )}
                <span
                  className="text-xs font-bold truncate max-w-[150px]"
                  style={{ color: 'rgba(10,10,10,0.58)', letterSpacing: '-0.015em' }}
                >
                  {item.brand} {item.name}
                </span>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Compare button */}
        <button
          onClick={handleCompareClick}
          className="font-black rounded-full text-xs flex items-center gap-2.5 uppercase flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #E8001A 0%, #B8000F 100%)',
            color: '#fff',
            letterSpacing: '0.11em',
            boxShadow: '0 4px 18px rgba(217,0,18,0.38), inset 0 1px 0 rgba(255,255,255,0.16)',
            padding: '13px 30px',
            transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            border: 'none',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 28px rgba(217,0,18,0.52), inset 0 1px 0 rgba(255,255,255,0.16)';
            (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
            (e.currentTarget as HTMLElement).style.background = 'linear-gradient(135deg, #D90012 0%, #A80010 100%)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 18px rgba(217,0,18,0.38), inset 0 1px 0 rgba(255,255,255,0.16)';
            (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
            (e.currentTarget as HTMLElement).style.background = 'linear-gradient(135deg, #E8001A 0%, #B8000F 100%)';
          }}
        >
          Compare ({selectedItems.length})
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
